import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, Copy, ExternalLink, Loader2 } from 'lucide-react';
import type { OwnerSummary, PublicOwnerBranding } from '@pay/shared';
import { api } from '../../api/client';
import { SalgsvilkarTekst } from '../../components/SalgsvilkarTekst';

// Salgsvilkårene slik kunden ser dem, fra menyen i Pay-admin (Terje 2026-11-04). Velg eier for å se eierens vilkår og
// lenken som oppgis til Vipps; uten eier vises den generelle varianten (/vilkar). Eieren står i adressen (?eier=…),
// så siden kan lenkes til. Teksten er den samme komponenten som kundesiden bruker (components/SalgsvilkarTekst).
export default function Vilkar() {
  const [params, setParams] = useSearchParams();
  const eierId = params.get('eier') ?? '';
  const [eiere, setEiere] = useState<OwnerSummary[] | null>(null);
  const [owner, setOwner] = useState<PublicOwnerBranding | null>(null);
  const [laster, setLaster] = useState(false);
  const [kopiert, setKopiert] = useState(false);
  const [feil, setFeil] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<OwnerSummary[]>('/api/admin/owners')
      .then((l) => setEiere([...l].sort((a, b) => a.name.localeCompare(b.name, 'nb'))))
      .catch(() => setEiere([]));
  }, []);

  useEffect(() => {
    setFeil(null);
    if (!eierId) {
      setOwner(null);
      return;
    }
    setLaster(true);
    api
      .get<PublicOwnerBranding>(`/api/public/owners/${encodeURIComponent(eierId)}`)
      .then(setOwner)
      .catch(() => {
        setOwner(null);
        setFeil('Fant ikke eieren. Vilkårene under er den generelle varianten.');
      })
      .finally(() => setLaster(false));
  }, [eierId]);

  const sti = eierId ? `/vilkar/${eierId}` : '/vilkar';
  const lenke = `${window.location.origin}${sti}`;

  async function kopier() {
    try {
      await navigator.clipboard.writeText(lenke);
      setKopiert(true);
      setTimeout(() => setKopiert(false), 2000);
    } catch {
      setFeil('Kunne ikke kopiere. Merk lenken og kopier den selv.');
    }
  }

  return (
    <div className="p-6 max-w-4xl">
      <h1 className="text-2xl font-semibold mb-1">Salgsvilkår</h1>
      <p className="text-sm text-slate-500 mb-5">
        Vilkårene kunden godtar ved betaling. De er lenket fra betalingssiden og kvitteringen på e-post. Lenken til
        eierens vilkår oppgis til Vipps.
      </p>

      <section className="bg-white rounded-lg shadow border border-slate-200 p-5 mb-5 space-y-4">
        <label className="block max-w-md">
          <span className="block text-sm font-medium text-slate-700 mb-1">Eier</span>
          <select
            className="input"
            value={eierId}
            onChange={(e) => setParams(e.target.value ? { eier: e.target.value } : {})}
            disabled={eiere === null}
          >
            <option value="">Generelle vilkår (uten eier)</option>
            {(eiere ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
                {o.active ? '' : ' (inaktiv)'}
              </option>
            ))}
          </select>
        </label>

        <div>
          <div className="text-sm font-medium text-slate-700 mb-1">Lenke</div>
          <div className="flex flex-wrap items-center gap-2">
            <code className="text-sm bg-slate-100 rounded px-2 py-1 break-all">{lenke}</code>
            <button
              type="button"
              onClick={kopier}
              className="flex items-center gap-1 text-sm border border-slate-300 rounded px-2 py-1 hover:bg-slate-50"
            >
              {kopiert ? <Check size={14} /> : <Copy size={14} />} {kopiert ? 'Kopiert' : 'Kopier'}
            </button>
            <a
              href={sti}
              target="_blank"
              rel="noopener"
              className="flex items-center gap-1 text-sm text-brand hover:underline"
            >
              <ExternalLink size={14} /> Åpne som kunden ser den
            </a>
          </div>
        </div>

        {owner && !owner.address && (
          <div role="status" className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Adressen til {owner.name} mangler i vilkårene. Fyll den inn på{' '}
            <Link to={`/admin/owners/${owner.id}`} className="underline">
              eiersiden
            </Link>
            .
          </div>
        )}
        {feil && <div className="text-sm text-red-600">{feil}</div>}
      </section>

      <div className="max-w-2xl">
        {laster ? (
          <div className="py-10 flex justify-center">
            <Loader2 className="animate-spin text-slate-400" size={28} />
          </div>
        ) : (
          <SalgsvilkarTekst owner={owner} />
        )}
      </div>
    </div>
  );
}
